import React, { useState } from "react";
import {
  Box,
  Button,
  Stack,
  Typography,
  Alert,
  CircularProgress,
  Grid,
  Chip,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HowToVoteOutlinedIcon from "@mui/icons-material/HowToVoteOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { toast } from "react-toastify";
import { createVotePurchaseCheckout } from "../../../api/votePurchase";
import { votePaymentProviderLabel } from "../../../utils/competitionVotePurchase";

export function currencySymbol(code) {
  const c = (code || "GBP").toUpperCase();
  if (c === "GBP") return "£";
  if (c === "EUR") return "€";
  if (c === "USD") return "$";
  return `${c} `;
}

function packFeatures(votes, providerLabel) {
  return [
    `${votes} votes added instantly to your wallet`,
    "Use votes on any approved competition video or image",
    "Works in the app and on the web",
    `Secure checkout powered by ${providerLabel}`,
  ];
}

export function PricingCard({
  pack,
  symbol,
  currency,
  enabled,
  loading,
  onBuy,
  highlighted,
  index,
  providerLabel,
  featureList,
}) {
  const price = Number(pack.price);
  const perVote = pack.votes > 0 ? (price / pack.votes).toFixed(2) : price;
  const label = `${pack.votes} votes`;

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 3,
        p: { xs: 2.5, md: 3 },
        bgcolor: highlighted ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.03)",
        border: highlighted
          ? "1px solid rgba(255,205,31,0.45)"
          : "1px solid rgba(255,255,255,0.1)",
        boxShadow: highlighted
          ? "0 24px 48px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,205,31,0.08)"
          : "0 8px 32px rgba(0,0,0,0.2)",
        transform: highlighted ? { md: "scale(1.03)" } : "none",
        transition: "transform 0.25s ease, box-shadow 0.25s ease",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {highlighted ? (
        <Chip
          label="Most popular"
          size="small"
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            bgcolor: "#FFCD1F",
            color: "#111",
            fontWeight: 700,
            fontSize: 11,
          }}
        />
      ) : null}

      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: highlighted
            ? "rgba(255,205,31,0.15)"
            : "rgba(255,255,255,0.06)",
          mb: 2,
        }}
      >
        <HowToVoteOutlinedIcon
          sx={{ color: highlighted ? "#FFCD1F" : "rgba(255,255,255,0.7)" }}
        />
      </Box>

      <Typography
        sx={{
          fontWeight: 700,
          fontSize: 13,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: highlighted ? "#FFCD1F" : "rgba(255,255,255,0.5)",
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontWeight: 800,
          fontFamily: "Montserrat, sans-serif",
          fontSize: { xs: 26, md: 28 },
          color: "#fff",
          lineHeight: 1.1,
          mb: 0.5,
        }}
      >
        {pack.votes} votes
      </Typography>
      <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: 14, mb: 2.5 }}>
        {index === 0
          ? "Try a small boost"
          : index === 1
            ? "Great for active fans"
            : "Maximum impact"}
      </Typography>

      <Stack direction="row" alignItems="baseline" gap={0.75} mb={0.5}>
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: { xs: 36, md: 42 },
            color: "#fff",
            lineHeight: 1,
          }}
        >
          {symbol}
          {price.toFixed(2)}
        </Typography>
        <Typography sx={{ color: "rgba(255,255,255,0.45)", fontSize: 14 }}>
          {currency}
        </Typography>
      </Stack>
      <Typography sx={{ color: "rgba(255,255,255,0.4)", fontSize: 13, mb: 2.5 }}>
        {symbol}
        {perVote} per vote
      </Typography>

      <Button
        fullWidth
        variant="contained"
        disabled={!enabled || loading}
        onClick={() => onBuy(pack)}
        sx={{
          textTransform: "none",
          fontWeight: 700,
          fontSize: 15,
          py: 1.35,
          borderRadius: 2,
          mb: 2.5,
          bgcolor: highlighted ? "#FFCD1F" : "#fff",
          color: "#111",
          boxShadow: highlighted ? "0 8px 24px rgba(255,205,31,0.25)" : "none",
          "&:hover": {
            bgcolor: highlighted ? "#e6b800" : "rgba(255,255,255,0.92)",
          },
          "&.Mui-disabled": {
            bgcolor: "rgba(255,255,255,0.08)",
            color: "rgba(255,255,255,0.35)",
          },
        }}
      >
        {loading ? (
          <CircularProgress size={22} sx={{ color: "#111" }} />
        ) : (
          `Get ${pack.votes} votes`
        )}
      </Button>

      <Box
        sx={{
          borderTop: "1px solid rgba(255,255,255,0.08)",
          pt: 2,
          flex: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 600,
            color: "rgba(255,255,255,0.45)",
            mb: 1.5,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
          }}
        >
          Includes
        </Typography>
        <Stack spacing={1.2}>
          {(featureList || packFeatures(pack.votes, providerLabel)).map((feature) => (
            <Stack key={feature} direction="row" alignItems="flex-start" gap={1}>
              <CheckCircleOutlineIcon
                sx={{
                  fontSize: 18,
                  color: highlighted ? "#FFCD1F" : "rgba(255,255,255,0.45)",
                  mt: 0.15,
                  flexShrink: 0,
                }}
              />
              <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.72)", lineHeight: 1.45 }}>
                {feature}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Box>
    </Box>
  );
}

export default function CompetitionBuyVotesContent({
  competitionId,
  competitionName,
  votePurchase,
  token,
  remainingVotes,
  showIntro = true,
  variant = "compact",
}) {
  const [loadingPack, setLoadingPack] = useState(null);

  const enabled = votePurchase?.enabled;
  const currency = (votePurchase?.currency || "GBP").toUpperCase();
  const symbol = currencySymbol(currency);
  const packs = votePurchase?.packs || [];
  const providerLabel = votePaymentProviderLabel(votePurchase);
  const appDeepLink =
    process.env.REACT_APP_VOTE_PURCHASE_DEEP_LINK || "mynetgoals://";

  const handleBuy = async (pack) => {
    if (!token) {
      toast.error("Please log in to purchase votes.");
      return;
    }
    setLoadingPack(pack.votes);
    try {
      const checkoutOpts = { competitionId, packVotes: pack.votes };
      if (variant === "page") {
        checkoutOpts.returnDeepLink = appDeepLink;
      }
      const res = await createVotePurchaseCheckout(checkoutOpts, token);
      if (res.success && res.data?.checkoutUrl) {
        window.location.href = res.data.checkoutUrl;
        return;
      }
      toast.error(res.message || "Could not start checkout");
    } catch (e) {
      toast.error(
        e?.response?.data?.message || "Failed to start payment. Try again."
      );
    } finally {
      setLoadingPack(null);
    }
  };

  const alerts = (
    <>
      {Number(remainingVotes) > 0 ? (
        <Alert
          severity="info"
          sx={{
            mb: 3,
            bgcolor: "rgba(59,130,246,0.12)",
            color: "#fff",
            border: "1px solid rgba(59,130,246,0.25)",
            "& .MuiAlert-icon": { color: "#93c5fd" },
          }}
        >
          You already have {remainingVotes} vote
          {Number(remainingVotes) === 1 ? "" : "s"} in your wallet. You can
          still purchase additional packs below.
        </Alert>
      ) : null}

      {!enabled ? (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Payments for votes are currently suspended for this competition.
        </Alert>
      ) : null}
    </>
  );

  const footer = (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      alignItems="center"
      justifyContent="center"
      gap={1}
      sx={{ mt: variant === "page" ? 5 : 2 }}
    >
      <LockOutlinedIcon sx={{ fontSize: 16, color: "rgba(255,255,255,0.35)" }} />
      <Typography
        sx={{
          fontSize: 13,
          color: "rgba(255,255,255,0.45)",
          lineHeight: 1.5,
          textAlign: "center",
        }}
      >
        Secure payment via {providerLabel}. No commitment — cancel anytime before
        completing checkout.
      </Typography>
    </Stack>
  );

  if (variant === "page") {
    const popularVotes = packs.find((p) => p.votes === 10)?.votes ?? packs[1]?.votes;

    return (
      <>
        {alerts}

        <Grid container spacing={2.5} alignItems="stretch">
          {packs.map((pack, index) => (
            <Grid item xs={12} md={4} key={pack.votes}>
              <PricingCard
                pack={pack}
                symbol={symbol}
                currency={currency}
                enabled={enabled}
                loading={loadingPack === pack.votes}
                onBuy={handleBuy}
                highlighted={pack.votes === popularVotes}
                index={index}
                providerLabel={providerLabel}
              />
            </Grid>
          ))}
        </Grid>

        {footer}
      </>
    );
  }

  return (
    <>
      {showIntro ? (
        <Typography sx={{ color: "rgba(255,255,255,0.78)", fontSize: 14, mb: 2 }}>
          {competitionName
            ? `Purchase vote packs for ${competitionName}. Votes are added to your wallet and can be used across the app.`
            : "Purchase vote packs. Votes are added to your wallet."}
        </Typography>
      ) : null}

      {alerts}

      <Stack spacing={1.5}>
        {packs.map((pack) => (
          <Box
            key={pack.votes}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              p: 2,
              borderRadius: 2,
              border: "1px solid #414466",
              bgcolor: "rgba(255,255,255,0.04)",
            }}
          >
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: 18 }}>
                {pack.votes} votes
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: 13 }}>
                {symbol}
                {Number(pack.price).toFixed(2)}
              </Typography>
            </Box>
            <Button
              variant="contained"
              disabled={!enabled || loadingPack != null}
              onClick={() => handleBuy(pack)}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                bgcolor: "#FFCD1F",
                color: "#111",
                minWidth: 96,
                "&:hover": { bgcolor: "#e6b800" },
                "&.Mui-disabled": {
                  bgcolor: "#343759",
                  color: "rgba(255,255,255,0.5)",
                },
              }}
            >
              {loadingPack === pack.votes ? (
                <CircularProgress size={22} sx={{ color: "#111" }} />
              ) : (
                "Buy"
              )}
            </Button>
          </Box>
        ))}
      </Stack>

      {footer}
    </>
  );
}
